/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Open Source Version is distributed with Sencha Ext JS pursuant to a FLOSS Exception agreed upon
 * between Sonatype, Inc. and Sencha Inc. Sencha Ext JS is licensed under GPL v3 and cannot be redistributed as part of a
 * closed source work.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import {actions, assign, createMachine} from 'xstate';

import {fetchPermissions, fetchStateValues} from './stateApi';

const {choose, raise} = actions;

/** Consecutive poll failures before polling stops, as in the ExtJS State controller. */
export const MAX_DISCONNECT_WARNINGS = 3;

/**
 * Applies state entries the way NX.controller.State.setValues() does: a {hash, value} wrapper carries a hash, any
 * other value has none, and a null or undefined value removes the key.
 * @returns {{values: Object, hashes: Object}} new maps; untouched keys keep their value references
 */
export function applyEntries(values, hashes, entries) {
  const nextValues = {...values};
  const nextHashes = {...hashes};

  Object.entries(entries ?? {}).forEach(([key, entry]) => {
    let value = entry;
    let hash;
    if (entry && typeof entry === 'object' && entry.hash !== undefined && entry.value !== undefined) {
      hash = entry.hash;
      value = entry.value;
    }

    if (value === undefined || value === null) {
      delete nextValues[key];
      delete nextHashes[key];
      return;
    }

    nextValues[key] = value;
    if (hash) {
      nextHashes[key] = hash;
    }
    else {
      delete nextHashes[key];
    }
  });

  return {values: nextValues, hashes: nextHashes};
}

/**
 * Builds the initial context from the app.js state seed. Adds the values NX.controller.State.onLaunch() sets on
 * top of the seed.
 */
export function contextFromSeed(seed) {
  const {values, hashes} = applyEntries({}, {}, seed);
  const uiSettings = values.uiSettings ?? {};

  return {
    values: {
      ...values,
      browserSupported: true,
      debug: Boolean(uiSettings.debugAllowed) && window.location.search === '?debug',
      receiving: false
    },
    hashes,
    // Set of granted permission ids; undefined until loaded, like NX.Permissions.permissions
    permissions: undefined,
    // counts permission loads; listeners are told about every load, as the ExtJS Permissions controller does
    permissionsLoads: 0,
    failures: 0
  };
}

function userIdentity(user) {
  return user ? `user:${user.id}` : 'anonymous';
}

/**
 * Mirrors NX.controller.State: the user changes when it appears, disappears or changes id. A new hash for the same
 * user is applied without counting as a change.
 */
function userChangedBy(context, entries) {
  if (!entries || !('user' in entries)) {
    return false;
  }
  const {values} = applyEntries({user: context.values.user}, {}, {user: entries.user});
  return userIdentity(context.values.user) !== userIdentity(values.user);
}

/** Poll interval in ms from uiSettings: the authenticated or anonymous interval, in seconds. */
export function pollIntervalMs(context) {
  const uiSettings = context.values.uiSettings ?? {};
  const seconds = context.values.user ? uiSettings.statusIntervalAuthenticated : uiSettings.statusIntervalAnonymous;
  return (seconds ?? 0) * 1000;
}

/**
 * Nexus One state: user, license, edition, permissions and every other rapture state value.
 *
 * Two regions run in parallel. `stateValues` polls rapture_State_get on the uiSettings interval, sending the hashes it
 * holds so only changed values come back; it stops after MAX_DISCONNECT_WARNINGS consecutive failures until a
 * REFRESH. The machine is created from the seed app.js already fetched, complete with hashes, so the region starts
 * out `waiting` for the interval instead of polling again immediately. `permissions` loads rapture_Security.getPermissions
 * at start and again whenever the user changes, as the ExtJS Permissions controller does.
 *
 * Events: REFRESH (poll now), SET_VALUE {key, value} (local write, as NX.State.setValue), SET_USER {user}.
 */
export default createMachine(
    {
      id: 'appState',
      type: 'parallel',
      predictableActionArguments: true,
      context: contextFromSeed({}),
      on: {
        SET_VALUE: {actions: 'setValue'},
        SET_USER: {actions: ['raiseIfUserChanged', 'setUser']}
      },
      states: {
        stateValues: {
          // The seed already carries fresh values and hashes (see contextFromSeed), so the first poll waits for the
          // interval instead of re-fetching what app.js just delivered. A user change still re-enters `.fetching`
          // directly below, because a new user needs fresh state right away.
          initial: 'waiting',
          // Re-entering the region cancels a poll in flight, so a response for the previous user is dropped
          on: {
            USER_CHANGED: {target: '.fetching', internal: false}
          },
          states: {
            fetching: {
              invoke: {
                id: 'fetchState',
                src: 'fetchState',
                onDone: {
                  target: 'waiting',
                  actions: ['raiseIfUserChanged', 'applyState']
                },
                onError: [
                  {
                    target: 'waiting',
                    cond: 'isLicenseRequired',
                    actions: 'markLicenseUninstalled'
                  },
                  {
                    target: 'disconnected',
                    cond: 'isLastFailure',
                    actions: 'recordFailure'
                  },
                  {
                    target: 'waiting',
                    actions: 'recordFailure'
                  }
                ]
              }
            },
            waiting: {
              always: {target: 'paused', cond: 'isPollingDisabled'},
              after: {POLL_INTERVAL: 'fetching'},
              on: {REFRESH: 'fetching'}
            },
            // uiSettings interval is 0: only explicit refreshes
            paused: {
              on: {REFRESH: 'fetching'}
            },
            disconnected: {
              on: {REFRESH: 'fetching'}
            }
          }
        },
        permissions: {
          initial: 'loading',
          on: {
            // Fail closed: clear the previous user's permissions as part of the transition itself, not only on a
            // subsequent failed reload, so checkPermission() never leaks the old user's permissions while the new
            // load is in flight (no privilege escalation).
            USER_CHANGED: {target: '.loading', internal: false, actions: 'clearPermissions'}
          },
          states: {
            loading: {
              invoke: {
                id: 'fetchPermissions',
                src: 'fetchPermissions',
                onDone: {target: 'loaded', actions: 'setPermissions'},
                // Fail closed: a failed reload after a user change must not leave the previous user's permissions
                onError: {target: 'failed', actions: 'clearPermissions'}
              }
            },
            loaded: {},
            failed: {}
          }
        }
      }
    },
    {
      actions: {
        applyState: assign((context, event) => {
          const {values, hashes} = applyEntries(context.values, context.hashes, event.data);
          return {
            values: {...values, receiving: true},
            hashes,
            failures: 0
          };
        }),

        recordFailure: assign({
          values: (context) => ({...context.values, receiving: false}),
          failures: (context) => context.failures + 1
        }),

        // NX.controller.State: a 402 means the license is gone, not that the server is. The round trip still
        // succeeded, so treat it like any other successful poll for the disconnect-failure counter and receiving.
        markLicenseUninstalled: assign({
          values: (context) => ({
            ...context.values,
            receiving: true,
            license: {...context.values.license, installed: false}
          }),
          failures: () => 0
        }),

        setValue: assign((context, {key, value}) => applyEntries(context.values, context.hashes, {[key]: value})),

        setUser: assign((context, {user}) => applyEntries(context.values, context.hashes, {user})),

        setPermissions: assign({
          permissions: (_, event) => event.data,
          permissionsLoads: (context) => context.permissionsLoads + 1
        }),

        clearPermissions: assign({
          permissions: () => undefined,
          permissionsLoads: (context) => context.permissionsLoads + 1
        }),

        raiseIfUserChanged: choose([
          {
            cond: (context, event) => userChangedBy(context, event.type === 'SET_USER' ? {user: event.user} : event.data),
            actions: raise('USER_CHANGED')
          }
        ])
      },
      guards: {
        isPollingDisabled: (context) => pollIntervalMs(context) <= 0,
        isLicenseRequired: (_, event) => event.data?.status === 402,
        isLastFailure: (context) => context.failures + 1 > MAX_DISCONNECT_WARNINGS
      },
      delays: {
        POLL_INTERVAL: (context) => pollIntervalMs(context)
      },
      services: {
        fetchState: (context) => fetchStateValues(context.hashes),
        fetchPermissions: () => fetchPermissions()
      }
    }
);
